import React, {
	type ReactElement,
	useCallback,
	useEffect,
	useState,
	type ReactNode,
	useMemo,
} from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import type { AnalyticsAttributes, Command } from '../../../../common/types';
import { useCommandPaletteExpandedCommand } from '../../../../controllers';

export const CommandBodyErrorBoundary = ({
	teamName,
	attributes,
	fallback,
	children,
	expandedCommand: expandedCommandOld,
}: {
	teamName?: string;
	attributes?: AnalyticsAttributes;
	fallback?: (props: { error: Error }) => ReactElement;
	children: ReactNode;
	expandedCommand?: Command;
}) => {
	const [{ expandedCommand: expandedCommandNew }] = ff(
		'compal-issue-actions-very-high-priority_f5w5r',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCommandPaletteExpandedCommand()
		: [{ expandedCommand: undefined }];

	const expandedCommand = useMemo(
		() =>
			ff('compal-issue-actions-very-high-priority_f5w5r') ? expandedCommandNew : expandedCommandOld,
		[expandedCommandNew, expandedCommandOld],
	);

	const [capturedError, setCapturedError] = useState<Error | undefined>();
	const [prevExpandedCommand, setPrevExpandedCommand] = useState(expandedCommand);
	const [hasReset, setHasReset] = useState(false);
	useEffect(() => {
		if (hasReset) {
			setCapturedError(undefined);
			setHasReset(false);
		}
	}, [hasReset]);

	const onRetry = useCallback(() => {
		setHasReset(true);
	}, []);

	// This is so that changing body triggers a reset if an error was caught and then the user goes back a level or to another command list
	// Without this, the body would not rerender, and the fallback would remain present
	useEffect(() => {
		if (capturedError && !hasReset && prevExpandedCommand !== expandedCommand) {
			onRetry();
		}
		setPrevExpandedCommand(expandedCommand);
	}, [capturedError, expandedCommand, hasReset, onRetry, prevExpandedCommand]);

	return (
		<JSErrorBoundary
			id="command.body"
			packageName="jiraCommandPalette"
			teamName={teamName || 'deliveroo'}
			attributes={attributes}
			onError={(_location, error) => {
				setCapturedError(error);
			}}
			fallback={fallback || 'unmount'}
			resetCaughtError={hasReset ? capturedError : undefined}
		>
			{children}
		</JSErrorBoundary>
	);
};
